import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import type { GetPageExperienceQuery } from '@/features/experiences/hooks/useGetPageExperience.generated';
import type { PageExperience } from '@/features/experiences/types';
import { createExperienceQueryKey } from '@/utils/queryKeys';

export type ExperienceCategory = PageExperience['categories'][number];
type CategoryMetadata = Pick<
  ExperienceCategory,
  'color' | 'colorName' | 'text' | 'sortOrder'
>;

/**
 * Optimistic cache helpers for an experience's categories, operating on the
 * `getExperience.categories` slice of the cached page-experience query. Mirrors
 * the schedule's `useScheduleCache` category methods.
 */
export const useCategoryCache = () => {
  const queryClient = useQueryClient();
  const experience = useEffectiveExperience();
  const queryKey = createExperienceQueryKey(experience.id);

  const setCategories = useCallback(
    (updater: (categories: ExperienceCategory[]) => ExperienceCategory[]) => {
      queryClient.setQueryData<GetPageExperienceQuery | undefined>(
        queryKey,
        (oldData) => {
          if (!oldData) {
            return oldData;
          }
          return {
            ...oldData,
            getExperience: {
              ...oldData.getExperience,
              categories: updater(oldData.getExperience.categories),
            },
          };
        }
      );
    },
    [queryClient, queryKey]
  );

  const getCategory = useCallback(
    (categoryId: string) => {
      const data = queryClient.getQueryData<GetPageExperienceQuery>(queryKey);
      return data?.getExperience.categories.find(
        (category) => category.id === categoryId
      );
    },
    [queryClient, queryKey]
  );

  const addCategory = useCallback(
    (categoryId: string, metadata: CategoryMetadata) => {
      setCategories((categories) => [
        { id: categoryId, ...metadata },
        ...categories,
      ]);
    },
    [setCategories]
  );

  const removeCategory = useCallback(
    (categoryId: string) => {
      setCategories((categories) =>
        categories.filter((category) => category.id !== categoryId)
      );
    },
    [setCategories]
  );

  const updateCategory = useCallback(
    (category: Pick<ExperienceCategory, 'id'> & Partial<CategoryMetadata>) => {
      setCategories((categories) =>
        categories.map((existing) =>
          existing.id === category.id ? { ...existing, ...category } : existing
        )
      );
    },
    [setCategories]
  );

  const setSortOrder = useCallback(
    (ordered: ExperienceCategory[]) => {
      setCategories(() =>
        ordered.map((category, index) => ({ ...category, sortOrder: index }))
      );
    },
    [setCategories]
  );

  return {
    getCategory,
    addCategory,
    removeCategory,
    updateCategory,
    setSortOrder,
  };
};
